import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full font-medium transition-colors',
  {
    variants: {
      variant: {
        default: 'bg-gray-100 text-gray-900',
        success: 'bg-green-100 text-green-800',
        warning: 'bg-yellow-100 text-yellow-800',
        error: 'bg-red-100 text-red-800',
        info: 'bg-blue-100 text-blue-800',
      },
      size: {
        sm: 'h-5 px-2 text-xs',
        md: 'h-6 px-3 text-sm',
        lg: 'h-7 px-4 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  customColor?: {
    bg: string;
    text: string;
  };
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, customColor, style, ...props }, ref) => {
    const customStyle = customColor
      ? {
          backgroundColor: customColor.bg,
          color: customColor.text,
          ...style,
        }
      : style;

    return (
      <span
        ref={ref}
        className={twMerge(
          badgeVariants({ variant: customColor ? undefined : variant, size }),
          className
        )}
        style={customStyle}
        {...props}
      />
    );
  }
);

Badge.displayName = 'Badge';
